import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M5 / M6 / M7 / M10 · Boîte de vitesses & séquences
// Composant unique paramétré par la prop `stepId` :
//   M5 : 1re → 2e → arrêt en 2e
//   M6 : 1re → 2e → 3e → arrêt en 3e
//   M7 : 1re → 2e → 3e → rétrogradation 3e→2e → arrêt en 2e
//  M10 : 1re → 2e → 3e → 4e → 5e → rétrogradation
// ══════════════════════════════════════════════════════════════════════════

const CONFIGS = {
  M5: {
    title: 'M5 · 1re → 2e (arrêt en 2e)',
    remc: 'C1f',
    sequence: [
      { gear: 1, speed: 20, action: "Démarrer en 1re · 1500 tr/min", tip: "Embrayage relâché progressivement, gaz doux." },
      { gear: 2, speed: 35, action: "Passer en 2e à ~20 km/h",       tip: "Débrayer FRANC, main droite vers le haut-droite, relâcher embrayage." },
      { gear: 2, speed: 20, action: "Freiner en 2e",                    tip: "Frein sans débrayer tant que le moteur ne cale pas." },
      { gear: 2, speed: 0,  action: "Arrêt en 2e",                       tip: "À l'arrêt : débrayer + point mort + frein à main." },
    ],
  },
  M6: {
    title: 'M6 · 1re → 3e (arrêt en 3e)',
    remc: 'C1f',
    sequence: [
      { gear: 1, speed: 20, action: "Démarrer en 1re",                 tip: "Embrayage doux, gaz progressif." },
      { gear: 2, speed: 35, action: "Passer en 2e à ~20 km/h",           tip: "Levier vers le haut-droite." },
      { gear: 3, speed: 55, action: "Passer en 3e à ~40 km/h",           tip: "Levier vers le bas depuis 2e (glisse rapide)." },
      { gear: 3, speed: 30, action: "Freiner en 3e",                     tip: "Freinage progressif, sans débrayer." },
      { gear: 3, speed: 0,  action: "Arrêt en 3e",                       tip: "À l'arrêt : débrayer + point mort." },
    ],
  },
  M7: {
    title: 'M7 · Rétrograder 3e → 2e (arrêt en 2e)',
    remc: 'C1f',
    sequence: [
      { gear: 1, speed: 20, action: "Démarrer en 1re",                 tip: "" },
      { gear: 2, speed: 35, action: "Monter en 2e",                     tip: "" },
      { gear: 3, speed: 55, action: "Monter en 3e",                     tip: "" },
      { gear: 2, speed: 30, action: "RÉTROGRADER 3e → 2e",              tip: "Freinage AVANT de rétrograder, puis débrayer + levier 2e + relâcher." },
      { gear: 2, speed: 0,  action: "Arrêt en 2e",                       tip: "Frein + débrayer + point mort." },
    ],
  },
  M10: {
    title: 'M10 · Passages jusqu\'en 5e + rétrogradation',
    remc: 'C1f',
    sequence: [
      { gear: 1, speed: 20, action: "1re",  tip: "Démarrage. 0 → 20 km/h." },
      { gear: 2, speed: 35, action: "2e",   tip: "20 → 35 km/h." },
      { gear: 3, speed: 55, action: "3e",   tip: "35 → 55 km/h. Zone urbaine étendue." },
      { gear: 4, speed: 75, action: "4e",   tip: "55 → 75 km/h. Route hors agglo." },
      { gear: 5, speed: 105, action: "5e",  tip: "75 → 105 km/h. Vitesse de croisière." },
      { gear: 3, speed: 60, action: "RÉTRO 5e → 3e", tip: "Grosses rétrogradations autorisées : freinage franc AVANT changement." },
    ],
  },
};

// Positions des rapports sur la grille H (viewBox référentiel)
const GEAR_POS = {
  1: { x: 90, y: 60,  label: '1' },
  2: { x: 90, y: 200, label: '2' },
  3: { x: 200, y: 60, label: '3' },
  4: { x: 200, y: 200, label: '4' },
  5: { x: 310, y: 60, label: '5' },
  R: { x: 310, y: 200, label: 'R' },
  N: { x: 200, y: 130, label: 'N' },
};

export default function BoiteVitessesSvg({ stepId = 'M5' }) {
  const cfg = CONFIGS[stepId] || CONFIGS.M5;
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) return undefined;
    timerRef.current = setInterval(() => {
      setIdx((i) => {
        if (i >= cfg.sequence.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, 1600);
    return () => clearInterval(timerRef.current);
  }, [playing, cfg.sequence.length]);

  // Reset idx quand on change de step
  useEffect(() => { setIdx(0); setPlaying(false); }, [stepId]);

  const step = cfg.sequence[idx];
  const reset = () => { setIdx(0); setPlaying(false); };
  const play = () => { if (idx >= cfg.sequence.length - 1) setIdx(0); setPlaying(true); };
  const next = () => setIdx((i) => Math.min(cfg.sequence.length - 1, i + 1));
  const gearPos = GEAR_POS[step.gear] || GEAR_POS.N;

  return (
    <div className="w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {!playing ? (
          <button type="button" onClick={play} data-testid={`${stepId.toLowerCase()}-play`} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-emerald-500 text-emerald-950 font-bold text-[12px] hover:bg-emerald-400">
            <Play size={13} /> Jouer la séquence
          </button>
        ) : (
          <button type="button" onClick={() => setPlaying(false)} data-testid={`${stepId.toLowerCase()}-pause`} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-500 text-amber-950 font-bold text-[12px]">
            <Pause size={13} /> Pause
          </button>
        )}
        <button type="button" onClick={next} disabled={idx >= cfg.sequence.length - 1} data-testid={`${stepId.toLowerCase()}-next`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 text-[12px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
          Étape →
        </button>
        <button type="button" onClick={reset} data-testid={`${stepId.toLowerCase()}-reset`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold">
          <RotateCcw size={13} /> Reset
        </button>
        <div className="ml-auto text-white/50 text-[11px] font-mono-spec">Étape {idx + 1} / {cfg.sequence.length}</div>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label={cfg.title}>
            <defs>
              <linearGradient id="bv-panel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0b1120" />
              </linearGradient>
              <radialGradient id="bv-glow">
                <stop offset="0%" stopColor="#22c55e" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
              </radialGradient>
            </defs>

            <rect width={800} height={700} fill="#0b1120" />

            {/* ═══ GAUCHE — Grille H de la boîte de vitesses ═══ */}
            <g transform="translate(160 120)">
              <rect x={-30} y={-30} width={400} height={310} rx={16} fill="url(#bv-panel)" stroke="#334155" strokeWidth={2} />
              <text x={170} y={-5} textAnchor="middle" fill="#94a3b8" fontSize={11} fontFamily="monospace" fontWeight="bold">GRILLE DE VITESSES</text>

              {/* Traits guides */}
              <line x1={90} y1={30} x2={90} y2={220} stroke="#334155" strokeWidth={3} />
              <line x1={200} y1={30} x2={200} y2={220} stroke="#334155" strokeWidth={3} />
              <line x1={310} y1={30} x2={310} y2={220} stroke="#334155" strokeWidth={3} />
              <line x1={90} y1={130} x2={310} y2={130} stroke="#334155" strokeWidth={3} />

              {/* Points de rapport */}
              {Object.entries(GEAR_POS).filter(([k]) => k !== 'N').map(([k, p]) => {
                const isActive = String(step.gear) === k;
                return (
                  <g key={k} transform={`translate(${p.x} ${p.y})`}>
                    {isActive && <circle r={28} fill="url(#bv-glow)"><animate attributeName="r" values="24;36;24" dur="1.6s" repeatCount="indefinite" /></circle>}
                    <circle r={20} fill={isActive ? '#22c55e' : '#0b1120'} stroke={isActive ? '#fff' : '#475569'} strokeWidth={2.5} />
                    <text y={6} textAnchor="middle" fill={isActive ? '#0b1120' : '#94a3b8'} fontSize={18} fontWeight="900" fontFamily="serif">{p.label}</text>
                  </g>
                );
              })}

              {/* Levier animé qui va vers la position active */}
              <g style={{ transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)' }} transform={`translate(${gearPos.x} ${gearPos.y})`}>
                <line x1={0} y1={0} x2={0} y2={-40} stroke="#71717a" strokeWidth={5} />
                <circle cx={0} cy={-42} r={14} fill="#525b6b" stroke="#0b1120" strokeWidth={2} />
                <text y={-38} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="900">{gearPos.label}</text>
              </g>
            </g>

            {/* ═══ DROITE — Compteur vitesse + tachymètre ═══ */}
            <g transform="translate(560 220)">
              {/* Compteur km/h grand */}
              <circle r={90} fill="#0b1120" stroke="#334155" strokeWidth={3} />
              <circle r={78} fill="none" stroke="#22c55e" strokeWidth={2} strokeDasharray="4 3" opacity={0.5} />
              {/* Aiguille */}
              <g style={{ transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)' }} transform={`rotate(${-135 + (step.speed / 120) * 270})`}>
                <line x1={0} y1={0} x2={0} y2={-72} stroke="#22c55e" strokeWidth={4} />
                <circle r={8} fill="#22c55e" />
              </g>
              <text y={30} textAnchor="middle" fill="#fff" fontSize={26} fontWeight="900" fontFamily="monospace">{step.speed}</text>
              <text y={50} textAnchor="middle" fill="#94a3b8" fontSize={11} fontFamily="monospace">km/h</text>
              {/* Graduations */}
              {[0, 30, 60, 90, 120].map((v) => {
                const angle = -135 + (v / 120) * 270;
                const rad = angle * Math.PI / 180;
                const x1 = 68 * Math.cos(rad); const y1 = 68 * Math.sin(rad);
                const x2 = 78 * Math.cos(rad); const y2 = 78 * Math.sin(rad);
                const tx = 55 * Math.cos(rad); const ty = 55 * Math.sin(rad);
                return (
                  <g key={v}>
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#94a3b8" strokeWidth={2} />
                    <text x={tx} y={ty + 4} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">{v}</text>
                  </g>
                );
              })}
            </g>

            {/* Timeline en bas */}
            <g transform="translate(80 500)">
              <text x={0} y={0} fill="#94a3b8" fontSize={11} fontFamily="monospace" fontWeight="bold">SÉQUENCE</text>
              {cfg.sequence.map((s, i) => (
                <g key={i} transform={`translate(${i * (640 / cfg.sequence.length)} 14)`}>
                  <rect x={0} y={0} width={(640 / cfg.sequence.length) - 8} height={42} rx={6}
                        fill={i === idx ? '#22c55e30' : i < idx ? '#22c55e15' : '#1e293b60'}
                        stroke={i === idx ? '#22c55e' : i < idx ? '#22c55e60' : '#334155'}
                        strokeWidth={i === idx ? 2 : 1} />
                  <text x={12} y={16} fill={i === idx ? '#fff' : '#94a3b8'} fontSize={10} fontWeight="bold" fontFamily="monospace">
                    #{i + 1} · {s.action.substring(0, 30)}
                  </text>
                  <text x={12} y={30} fill={i === idx ? '#22c55e' : '#64748b'} fontSize={9} fontFamily="monospace">
                    {s.gear} · {s.speed} km/h
                  </text>
                </g>
              ))}
            </g>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.65} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">{cfg.title}</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC {cfg.remc} — Boîte de vitesses</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid={`${stepId.toLowerCase()}-panel`}>
          <div className="rounded-xl border-2 border-emerald-500 bg-emerald-950/25 p-3.5">
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold text-emerald-300">Étape courante</div>
            <div className="mt-1 font-serif-editorial text-white text-[19px] leading-tight">{step.action}</div>
            <div className="mt-1.5 flex gap-3 text-[11px] font-mono-spec">
              <span className="text-white/60">Rapport : <b className="text-emerald-200">{step.gear}</b></span>
              <span className="text-white/60">Vitesse : <b className="text-emerald-200">{step.speed} km/h</b></span>
            </div>
          </div>

          {step.tip && (
            <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
              <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Consigne</div>
                <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{step.tip}</p>
              </div>
            </div>
          )}

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste global</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">
                <b>Débrayage franc et bref</b> · main droite décidée sur le levier · relâche embrayage progressif tout en accélérant doucement · regard TOUJOURS sur la route (jamais sur le levier).
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Piège classique</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">
                Regarder le levier → sortir de trajectoire. Rester en 1re trop longtemps → moteur qui hurle (bruit + consommation). Rétrograder AVANT de freiner → surcompensation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
